import { Box, Button, HStack, Text, VStack, Switch } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "../../trpc/client";
import { useMerchant } from "../../hooks/useMerchant";
import useNumberFormatter from "../../hooks/useNumberFormatter";
import { toaster } from "../ui/toaster";

const FIXED_METHODS = [
  {
    key: "pse",
    label: "PSE",
    badgeBg: "#FFE7D6",
    badgeColor: "#F0873A",
  },
  {
    key: "card",
    label: "Tarjeta",
    badgeBg: "#DFEAFF",
    badgeColor: "#6887D8",
  },
] as const;

const TOGGLEABLE_METHODS = [
  {
    key: "nequiButton",
    label: "Nequi",
    badgeBg: "#EDEBFF",
    badgeColor: "#7B61FA",
  },
  {
    key: "bancolombiaButton",
    label: "Bancolombia",
    badgeBg: "#FFF7D6",
    badgeColor: "#E6AE22",
  },
  {
    key: "breb",
    label: "Bre-B",
    badgeBg: "#E3FFE4",
    badgeColor: "#3CB18B",
  },
] as const;

type ToggleableMethodKey = (typeof TOGGLEABLE_METHODS)[number]["key"];

const VAT_RATE = 0.19;
const WITHHOLDING_RATE = 0.035;

const feeNetFactor = (appliesWithholdingTax: boolean) =>
  1 + VAT_RATE - (appliesWithholdingTax ? WITHHOLDING_RATE : 0);

const toOriginalFee = (netFee: number, appliesWithholdingTax: boolean) =>
  Math.round(netFee / feeNetFactor(appliesWithholdingTax));

const toNetFee = (originalFee: number, appliesWithholdingTax: boolean) =>
  Math.round(originalFee * feeNetFactor(appliesWithholdingTax));

const GRID_COLUMNS = "140px 1fr 1fr 88px 44px";

const MethodRow = ({
  label,
  badgeBg,
  badgeColor,
  fee,
  appliesWithholdingTax,
  active,
  formatCurrency,
  toggle,
}: {
  label: string;
  badgeBg: string;
  badgeColor: string;
  fee: number;
  appliesWithholdingTax: boolean;
  active: boolean;
  formatCurrency: (value: number) => string;
  toggle?: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
  };
}) => {
  const originalFee = toOriginalFee(fee, appliesWithholdingTax);
  const netFee = toNetFee(originalFee, appliesWithholdingTax);

  return (
    <Box
      borderWidth="1px"
      borderColor={active ? "gray.200" : "gray.100"}
      borderRadius="md"
      bg={active ? "white" : "gray.50"}
      px={4}
      py={3.5}
      display="grid"
      gridTemplateColumns={GRID_COLUMNS}
      columnGap={4}
      alignItems="center"
      transition="background 0.15s ease, border-color 0.15s ease"
    >
      <Box
        px={2.5}
        py={1}
        borderRadius="md"
        bg={badgeBg}
        color={badgeColor}
        fontSize="xs"
        fontWeight={700}
        width="fit-content"
      >
        {label}
      </Box>
      <Text fontSize="sm" fontWeight={600} fontFamily="mono">
        {formatCurrency(originalFee)}
      </Text>
      <Text fontSize="sm" color="gray.600" fontFamily="mono">
        {formatCurrency(netFee)}
      </Text>
      <Text
        fontSize="xs"
        fontWeight={500}
        color={active ? "green.600" : "gray.500"}
        textAlign="right"
      >
        {active ? "Activo" : "Inactivo"}
      </Text>
      <Box display="flex" justifyContent="flex-end">
        {toggle ? (
          <Switch.Root
            checked={toggle.checked}
            onCheckedChange={({ checked }) => toggle.onCheckedChange(checked)}
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
        ) : null}
      </Box>
    </Box>
  );
};

const PaymentMethodsTab = (): JSX.Element => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const formatCurrency = useNumberFormatter();

  const [toggles, setToggles] = useState<
    Partial<Record<ToggleableMethodKey, boolean>>
  >({});

  useEffect(() => {
    if (merchant)
      setToggles(
        Object.fromEntries(
          TOGGLEABLE_METHODS.filter(
            ({ key }) => merchant.paymentMethods[key]
          ).map(({ key }) => [key, merchant.paymentMethods[key]!.active])
        )
      );
  }, [merchant?.id]);

  const { mutateAsync: save, isPending } = useMutation({
    ...trpc.merchants.setPaymentMethodToggles.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.merchants.list.queryKey(),
      });
      toaster.create({
        title: "Cambios guardados",
        description: "Los métodos de pago se actualizaron con éxito.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    onError: () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al guardar los cambios.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    },
  });

  const toggleableMethods = TOGGLEABLE_METHODS.filter(
    ({ key }) => merchant?.paymentMethods[key]
  );

  if (!merchant) return <></>;

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <Box mb={3} minHeight="44px">
          <Text fontWeight={600}>Métodos de pago</Text>
          <Text fontSize="sm" color="gray.500">
            Consulta las comisiones y activa o desactiva los métodos opcionales
          </Text>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={GRID_COLUMNS}
          columnGap={4}
          px={4}
          pb={1}
        >
          <Text fontSize="xs" color="gray.500" fontWeight={500}>
            Método
          </Text>
          <Text fontSize="xs" color="gray.500" fontWeight={500}>
            Comisión
          </Text>
          <Text fontSize="xs" color="gray.500" fontWeight={500}>
            Neta (+ IVA)
          </Text>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight={500}
            textAlign="right"
          >
            Estado
          </Text>
          <Box />
        </Box>

        <VStack gap={2} align="stretch">
          {FIXED_METHODS.map(({ key, label, badgeBg, badgeColor }) => {
            const method = merchant.paymentMethods[key];
            return (
              <MethodRow
                key={key}
                label={label}
                badgeBg={badgeBg}
                badgeColor={badgeColor}
                fee={method.fee}
                appliesWithholdingTax={merchant.appliesWithholdingTax}
                active={method.active}
                formatCurrency={formatCurrency}
              />
            );
          })}

          {toggleableMethods.map(({ key, label, badgeBg, badgeColor }) => {
            const method = merchant.paymentMethods[key]!;
            const active = toggles[key] ?? false;
            return (
              <MethodRow
                key={key}
                label={label}
                badgeBg={badgeBg}
                badgeColor={badgeColor}
                fee={method.fee}
                appliesWithholdingTax={merchant.appliesWithholdingTax}
                active={active}
                formatCurrency={formatCurrency}
                toggle={{
                  checked: active,
                  onCheckedChange: (checked) =>
                    setToggles({ ...toggles, [key]: checked }),
                }}
              />
            );
          })}
        </VStack>
      </Box>

      {toggleableMethods.length > 0 && (
        <HStack justify="flex-end">
          <Button
            loading={isPending}
            onClick={() => save({ rentalsMerchantId: merchant.id, ...toggles })}
          >
            Guardar
          </Button>
        </HStack>
      )}
    </VStack>
  );
};

export default PaymentMethodsTab;
